import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Printer, X, FileDown } from 'lucide-react';
import { jsPDF } from 'jspdf';

const PrintQRModal = ({ isOpen, onClose, title, id, qrcodeData, codeEtablissement, role, classe }) => {
    if (!isOpen) return null;
    const handleGeneratePDF = () => {
        const doc = new jsPDF({
            orientation: 'p', // Portrait because 69x100 is typically portrait orientation for label rolls
            unit: 'mm',
            format: [50, 50]
        });

        if (qrcodeData) {
            const imgData = "data:image/png;base64," + qrcodeData;
            doc.addImage(imgData, 'PNG', 8, 3, 34, 34); // Centered horizontally (69-61)/2 = 4mm margin
        }

        // Add Establishment Code
        doc.setFontSize(10);
        doc.setTextColor(100);
        const etabText = codeEtablissement;
        const etabWidth = doc.getTextWidth(etabText);
        doc.text(etabText, (50 - etabWidth) / 2, 6);

        // Add Item ID
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.setFont(undefined, 'bold');
        const idText = `ID: ${id}`;
        const idWidth = doc.getTextWidth(idText);
        doc.text(idText, (50 - idWidth) / 2, 38);

        // Add Truncated Title
        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(80);
        const splitTitle = doc.splitTextToSize(title || "", 46); // 60mm width for text
        let yPos = 42
        splitTitle.forEach(line => {
            const lineWidth = doc.getTextWidth(line);
            doc.text(line, (50 - lineWidth) / 2, yPos);
            yPos += 4;
        });

        const pdfName = role != null ? `etiquette_${title}.pdf` : `etiquette_${title}_${id}.pdf`;
        doc.save(pdfName);
        window.open(doc.output('bloburl'), '_blank');
    };
    const handleGeneratePDFold = () => {
        // Create PDF with label dimensions (e.g., 50mm x 30mm)
        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: [50, 30]
        });

        // Add QR Code (Base64)
        if (qrcodeData) {
            const imgData = "data:image/png;base64," + qrcodeData;
            doc.addImage(imgData, 'PNG', 2, 2, 26, 26);
        }

        // Add Establishment Code
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(codeEtablissement || "Biblius", 30, 8);

        // Add Item ID
        doc.setFontSize(10);
        doc.setTextColor(0);
        doc.setFont(undefined, 'bold');
        doc.text(`ID: ${id}`, 30, 15);

        // Add Truncated Title / Name
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(80);
        const splitTitle = doc.splitTextToSize(title || "", 18);
        doc.text(splitTitle, 30, 20);

        // Add Role / Class if available
        if (role || classe) {
            doc.setFontSize(6);
            doc.setTextColor(120);
            const extraInfo = `${role || ""}${role && classe ? " - " : ""}${classe || ""}`;
            doc.text(extraInfo, 30, 26);
        }

        // Save the PDF
        doc.save(`etiquette_${id}.pdf`);
    };
    useEffect(() => {
        handleGeneratePDF();
        onClose();
    }, []);
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl p-8 w-full max-w-sm shadow-2xl text-center"
            >
                <div className="flex justify-end mb-4">
                    <button onClick={onClose} className="text-slate-400 hover:text-black">
                        <X size={24} />
                    </button>
                </div>

                <div className="bg-white p-4 rounded-xl border-2 border-black inline-block mb-6">
                    {qrcodeData ? (
                        <img src={"data:image/png;base64," + qrcodeData} alt="QR Code" style={{ width: '150px', height: '150px' }} />
                    ) : (
                        <div className="w-[150px] h-[150px] bg-gray-200 flex items-center justify-center text-gray-400">
                            No QR
                        </div>
                    )}
                </div>

                <h3 className="text-xl font-bold text-black mb-1">{title}</h3>
                <p className="text-slate-500 text-sm mb-4">ID: {id}</p>
                {codeEtablissement && <p className="text-slate-400 text-xs mb-8">{codeEtablissement}</p>}

                <div className="flex flex-col gap-3">
                    <button
                        onClick={handleGeneratePDF}
                        className="w-full bg-black text-white font-bold py-3 rounded-xl hover:bg-slate-800 transition-colors flex items-center justify-center gap-2 shadow-lg"
                    >
                        <FileDown size={20} />
                        <span>Générer Etiquette (PDF)</span>
                    </button>

                    <button
                        onClick={() => window.print()}
                        className="w-full bg-slate-100 text-slate-600 font-medium py-2 rounded-xl hover:bg-slate-200 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                        <Printer size={16} />
                        <span>Impression standard</span>
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default PrintQRModal;
